import { useTranslation } from 'react-i18next'
import CircleIcon from '~/assets/circle.svg?react'
import EyeEnabled from '~/assets/icons/eye-enabled.svg?react'
import EyeDisabled from '~/assets/icons/eye-disabled.svg?react'

import { AdaptationIcon } from '~/components/icons/AdaptationIcon'
import { MenuSelect } from '~/components/MenuSelect'
import { Text } from '~/components/typography'
import Slider from '../Slider'
import { cn, kFormatter } from '~/util'

import {
  SCENARIO_LESS_BAD_FILL_COLOR,
  VisibleLayers,
  adaptationOptionsForSite,
  DEM_SCENARIOS,
  COSMOS_RETURN_PERIODS,
  AVERTED_DAMAGES_BREAKS,
  BUILDING_DAMAGES_BREAKS,
  COLORMAP_PURPLE_BENEFITS,
  COLORMAP_RED,
  FLOOD_DEPTH_COLORMAP,
  FLOOD_DEPTH_BREAKS,
} from '~/constants'
import { useAppStore } from '~/hooks'
import ToggleSwitch from '~/components/Toggle/Switch'
import { Checkbox } from '~/components/inputs'
import { floodDepthLabels } from '~/util/floodContours'
import { track } from '~/analytics'
import type { VisibleLayer } from '~/types/dataModel'

export function Cosmos() {
  const { t } = useTranslation()
  const {
    toggleLayerVisibility,
    layerVisibility,
    setReturnPeriod,
    setDEM,
    setAdaptation,
    getCosmosScenario,
    DEM,
    adaptation,
    returnPeriod,
    cosmosSite,
    contourFloodEnabled,
    setContourFloodEnabled,
    avertedDamagesEnabled,
    setAvertedDamagesEnabled,
  } = useAppStore()

  const depthLabels = floodDepthLabels(FLOOD_DEPTH_BREAKS)
  const depthColors = depthLabels.map((_, i) => {
    const n = depthLabels.length
    if (n === 1) return FLOOD_DEPTH_COLORMAP[0]!
    const t = i / (n - 1)
    const idx = Math.round(t * (FLOOD_DEPTH_COLORMAP.length - 1))
    return FLOOD_DEPTH_COLORMAP[idx]!
  })

  const formattedReturnPeriods = COSMOS_RETURN_PERIODS.map((rp) => {
    switch (rp) {
      case '005':
        return { label: t('cosmosReturnPeriods.5'), value: rp }
      case '020':
        return { label: t('cosmosReturnPeriods.20'), value: rp }
      case '050':
        return { label: t('cosmosReturnPeriods.50'), value: rp }
      case '100':
        return { label: t('cosmosReturnPeriods.100'), value: rp }
      default:
        return { label: rp, value: rp }
    }
  })

  const siteAdaptationOptions = adaptationOptionsForSite(cosmosSite)
  const formattedAdaptations = siteAdaptationOptions.map(({ value }) => ({
    label: t(`adaptation.${value}`),
    value,
  }))

  const formattedYear = DEM_SCENARIOS.map((dem) => {
    switch (dem) {
      case '020':
        return { label: t('cosmosYears.2025'), value: dem }
      case '050':
        return { label: t('cosmosYears.2055'), value: dem }
      case '080':
        return { label: t('cosmosYears.2085'), value: dem }
      default:
        return { label: dem, value: dem }
    }
  })

  const scenario = getCosmosScenario()
  const invalidScenario = DEM && adaptation && returnPeriod && !scenario

  const selectedAdaptationLabel =
    siteAdaptationOptions.find(({ value }) => value === adaptation) != null
      ? t(`adaptation.${adaptation ?? '000'}`)
      : t('adaptation.label')

  const adaptationSelected = adaptation != null && adaptation !== '000'
  const showAvertedDamages = adaptationSelected && avertedDamagesEnabled
  const damageColormap = showAvertedDamages ? COLORMAP_PURPLE_BENEFITS : COLORMAP_RED
  const damageBreaks = showAvertedDamages ? AVERTED_DAMAGES_BREAKS : BUILDING_DAMAGES_BREAKS
  const colorPairs = damageBreaks.map((breakpoint, i) => {
    return [breakpoint, damageColormap[i]!] as [number, string]
  })

  const captureScenarioUpdate = (
    setting: 'return_period' | 'year' | 'adaptation',
    value: string
  ) => {
    track('scenario_updated', { setting, value })
  }

  const toggleLayer = (layer: VisibleLayer) => {
    const visible = !layerVisibility[layer]
    toggleLayerVisibility(layer)
    track('layer_visibility_toggled', { layer, visible })
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CircleIcon className="h-5 w-5" color={SCENARIO_LESS_BAD_FILL_COLOR} />
              <Text className="text-neutral-midfi-grey" level={3}>
                {t('legend.floodedAreas')}
              </Text>
            </div>
            <ToggleSwitch
              checked={contourFloodEnabled}
              onChange={(enabled) => {
                setContourFloodEnabled(enabled)
                track('flood_display_mode_selected', { mode: enabled ? 'depth' : 'extent' })
              }}
              left={t('legend.floodExtent')}
              right={t('legend.floodDepth')}
              level={4}
              labelClassName="font-normal"
              switchClassName="h-4 w-8 border"
              buttonClassName="h-3 w-3 translate-x-[1px] translate-y-[1px] group-data-[checked]:translate-x-[17px]"
            />
          </div>
          {contourFloodEnabled && (
            <div className="flex w-full items-start gap-px">
              {depthColors.map((color, i) => (
                <div
                  key={`${depthLabels[i]}-${color}`}
                  className="flex min-w-0 flex-1 flex-col items-center gap-0.5"
                >
                  <div
                    className="h-4 w-full border border-[#6F7983]/30"
                    style={{
                      backgroundColor: color,
                      borderRadius:
                        i === 0
                          ? '4px 0 0 4px'
                          : i === depthColors.length - 1
                            ? '0 4px 4px 0'
                            : '0',
                    }}
                  />
                  <Text
                    className="whitespace-nowrap text-center text-[10px] text-neutral-midfi-grey"
                    level={4}
                  >
                    {depthLabels[i]}
                  </Text>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5" viewBox="0 0 20 20">
              <line
                x1="2"
                y1="18"
                x2="18"
                y2="2"
                stroke="purple"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <Text className="text-neutral-midfi-grey" level={3}>
              {t('legend.roads')}
            </Text>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => toggleLayer(VisibleLayers.ROAD)}>
              {layerVisibility[VisibleLayers.ROAD] ? <EyeEnabled /> : <EyeDisabled />}
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className="h-5 w-5"
                style={{ backgroundColor: damageColormap[damageColormap.length - 1] }}
              />
              <Text className="text-neutral-midfi-grey" level={3}>
                {showAvertedDamages ? t('legend.avertedDamages') : t('legend.damages')}
              </Text>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={showAvertedDamages}
                onChange={(enabled) => {
                  setAvertedDamagesEnabled(enabled)
                  track('averted_damages_toggled', { enabled })
                }}
                disabled={!adaptationSelected}
              >
                <Text
                  level={4}
                  className={cn('text-neutral-midfi-grey', adaptationSelected ? '' : 'opacity-50')}
                >
                  {t('legend.averted')}
                </Text>
              </Checkbox>
              <button onClick={() => toggleLayer(VisibleLayers.BUILDING)}>
                {layerVisibility[VisibleLayers.BUILDING] ? <EyeEnabled /> : <EyeDisabled />}
              </button>
            </div>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center justify-center gap-2">
              <Text className="text-neutral-midfi-grey" level={3}>
                ${damageBreaks[0]}
              </Text>
              <svg width="94px" height="17px">
                <linearGradient id="damage-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  {colorPairs.map(([value, color]: [number, string], i: number) => (
                    <stop
                      key={value}
                      offset={`${(i * 100) / (colorPairs.length - 1)}%`}
                      stopColor={color}
                    />
                  ))}
                </linearGradient>
                <rect
                  width="100%"
                  height="100%"
                  fill="url(#damage-gradient)"
                  rx="8"
                  ry="8"
                  stroke="#6F7983"
                  strokeWidth="0.5"
                />
              </svg>
              <Text className="text-neutral-midfi-grey" level={3}>
                ${kFormatter(damageBreaks[damageBreaks.length - 1]!)}+
              </Text>
            </div>
            <Text className="mr-6 text-[10px] text-neutral-midfi-grey" level={4}>
              {`(${t('legend.logarithmicScale')})`}
            </Text>
          </div>
        </div>

        {adaptation && adaptation !== '000' && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AdaptationIcon adaptation={adaptation} />
              <Text className="text-neutral-midfi-grey" level={3}>
                {t('legend.adaptationLabel', { label: selectedAdaptationLabel })}
              </Text>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => toggleLayer(VisibleLayers.ADAPTATION)}>
                {layerVisibility[VisibleLayers.ADAPTATION] ? <EyeEnabled /> : <EyeDisabled />}
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="space-y-1.5 border-t border-neutral-midfi-grey/20 pt-3">
        <div className="space-y-4">
          <div className="flex flex-col items-center">
            <Text className="self-start text-neutral-midfi-grey" level={3}>
              {t('legend.returnPeriodYears')}
            </Text>
            <Slider
              options={formattedReturnPeriods}
              value={returnPeriod ?? '005'}
              onChange={(nextReturnPeriod) => {
                setReturnPeriod(nextReturnPeriod)
                captureScenarioUpdate('return_period', nextReturnPeriod)
              }}
              ariaLabel={t('legend.sliderReturnPeriod')}
            />
          </div>
          <div className="flex flex-col items-center">
            <Text className="self-start text-neutral-midfi-grey" level={3}>
              {t('legend.year')}
            </Text>
            <Slider
              options={formattedYear}
              value={DEM ?? '020'}
              onChange={(nextDEM) => {
                setDEM(nextDEM)
                captureScenarioUpdate('year', nextDEM)
              }}
              ariaLabel={t('legend.sliderYear')}
            />
          </div>
          <div className="space-y-1 pt-1 text-left">
            <Text className="text-neutral-midfi-grey" level={3}>
              {t('legend.adaptation')}
            </Text>
            <MenuSelect
              onChange={(option) => {
                setAdaptation(option.value)
                captureScenarioUpdate('adaptation', option.value)
              }}
              defaultValue={
                formattedAdaptations.find(({ value }) => value === (adaptation ?? '000')) ??
                formattedAdaptations[0]!
              }
              options={formattedAdaptations}
            />
          </div>
        </div>
      </div>

      {invalidScenario && (
        <Text className="mt-2 text-level-error" level={4}>
          {t('legend.invalidScenario')}
        </Text>
      )}
    </>
  )
}
